import { API_BASE, TOKEN_KEY } from './api-client.js'

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
}

interface LoginResponse {
  token: string
  user: AuthUser
}

/**
 * Authenticate against the API Gateway. Uses a direct fetch (not apiClient)
 * so the apiClient global 401 → /login redirect does not swallow a
 * wrong-credentials response on the login form itself.
 */
export async function login(email: string, password: string): Promise<AuthUser> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    })
  } catch {
    throw new Error('Cannot reach the API — is it running?')
  }

  if (res.status === 401) {
    throw new Error('Invalid email or password')
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string }
    throw new Error(body.message ?? `Login failed (${res.status})`)
  }

  const data = (await res.json()) as LoginResponse
  localStorage.setItem(TOKEN_KEY, data.token)
  storeUser(data.user)
  return data.user
}

const USER_KEY = 'bainner_user'

/**
 * Persist the signed-in user alongside the token.
 *
 * The JWT carries `userId`, `role` and `permissions` — and NO name or email (decode one and
 * see). There is also no `GET /auth/me`. So the only moment the app ever learns who the user
 * IS, is the login response — which was being thrown away. The workspace shell's profile row
 * (DESIGN-HANDOVER.md §8.1) needs a name, and inventing one from the Figma mockup ("John
 * Bainner") would be a fabricated identity on the user's own screen.
 *
 * This is a display convenience ONLY. It is not, and must never become, an authorisation
 * signal: it is plain localStorage the user can edit. `role` for gating still comes from the
 * signed token via `isAgencySession()`, and the real guarantee is the route's own `authorize()`.
 */
export function storeUser(user: AuthUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  } catch {
    // A full or blocked localStorage must not break signing in.
  }
}

/**
 * The stored user, or `null` when unknown — which is a real state, not an edge case: any session
 * that signed in before this was added has a token but no stored user. Callers must render an
 * honest unknown rather than a placeholder name.
 */
export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AuthUser>
    if (typeof parsed.name !== 'string' || typeof parsed.id !== 'string') return null
    return parsed as AuthUser
  } catch {
    return null
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY)
}

// JWT uses base64url (- _ , no padding); atob wants standard base64. Mirrors
// `pages/deliver/decodeToken.ts`'s technique for the delivery token.
function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  return atob(padded)
}

/**
 * Client-side decode of the main-app JWT's `role` claim (Epic 43 Phase 04 —
 * gates agency-only UI like `RatioThresholdPanel`). This does NOT verify the
 * signature — the API Gateway does that on every request; the route's own
 * `authorize(['agency'])` is the real guarantee. This is only so a
 * client/media-agency session (which never actually reaches this token —
 * those use the separate `/review/:token` and `/deliver/:token` flows, not
 * `bainner_token`) doesn't see a visible-but-403 control if it somehow did.
 */
export function isAgencySession(): boolean {
  // The whole body is defensive, not just the JSON.parse: a test harness that
  // mocks `api-client.js` without `TOKEN_KEY` makes even `getToken()` throw
  // (a partial-mock gap, not a real failure mode) — fail-safe to "not agency"
  // rather than crash the render, since hiding the panel on uncertainty is the
  // same conservative direction this check exists for in the first place.
  try {
    const token = getToken()
    if (!token) return false

    const parts = token.split('.')
    if (parts.length !== 3) return false

    const payload = JSON.parse(base64UrlDecode(parts[1] ?? '')) as Record<string, unknown>
    return payload['role'] === 'agency'
  } catch {
    return false
  }
}
